import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { 
  Menu, 
  X, 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  ShieldCheck, 
  ChevronRight, 
  Monitor,
  Cpu,
  Headset
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SiFacebook, SiX, SiInstagram, SiLinkedin } from 'react-icons/si';
import { ROUTE_PATHS, formatPhoneNumber } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface LayoutProps {
  children: React.ReactNode;
}

const CONTACT_DATA = {
  phone: '8005550199',
  emergencyPhone: '8005559900',
  email: 'support@techrepair2026.com',
  address: '452 Tech Plaza, Digital District, CA 94043',
  workingHours: '24/7 Support Available',
  coverageAreas: ['San Francisco', 'San Jose', 'Palo Alto', 'Oakland', 'Fremont']
};

export function Layout({ children }: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: ROUTE_PATHS.HOME },
    { name: 'Services', path: ROUTE_PATHS.SERVICES },
    { name: 'About Us', path: ROUTE_PATHS.ABOUT },
    { name: 'Contact', path: ROUTE_PATHS.CONTACT },
  ];

  return (
    <div className="flex min-h-screen flex-col font-sans">
      {/* Top Bar */}
      <div className="bg-primary text-primary-foreground py-2 hidden md:block">
        <div className="container mx-auto px-4 flex justify-between items-center text-xs font-medium">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <Phone className="w-3 h-3" />
              Emergency Support: {formatPhoneNumber(CONTACT_DATA.emergencyPhone)}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-3 h-3" />
              {CONTACT_DATA.workingHours}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link to={ROUTE_PATHS.CONTACT} className="hover:text-white/80 transition-colors">Book a Repair</Link>
            <span className="opacity-30">|</span>
            <div className="flex items-center gap-3">
              <SiFacebook className="w-3 h-3 cursor-pointer hover:text-white/80" />
              <SiX className="w-3 h-3 cursor-pointer hover:text-white/80" />
              <SiLinkedin className="w-3 h-3 cursor-pointer hover:text-white/80" />
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <header
        ref={headerRef}
        className={cn(
          "sticky top-0 z-50 w-full transition-all duration-300 border-b",
          isScrolled 
            ? "bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 py-2 shadow-sm border-border"
            : "bg-background py-4 border-transparent"
        )}
      >
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-2 group">
            <div className="bg-primary p-2 rounded-lg group-hover:rotate-12 transition-transform">
              <Cpu className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <span className="text-xl font-bold tracking-tight block leading-none text-foreground">
                TECH<span className="text-primary">RESCUE</span>
              </span>
              <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                Electronics & IT Support
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                className={({ isActive }) =>
                  cn(
                    "text-sm font-medium transition-colors hover:text-primary relative py-1",
                    isActive ? "text-primary" : "text-muted-foreground"
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {link.name}
                    {isActive && (
                      <motion.div
                        layoutId="nav-underline"
                        className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary"
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
            <Button asChild className="ml-4 shadow-md">
              <Link to={ROUTE_PATHS.CONTACT}>Request Service</Link>
            </Button>
          </nav>

          {/* Mobile Toggle */}
          <button
            className="md:hidden p-2 text-foreground"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background border-b border-border overflow-hidden"
          >
            <div className="container mx-auto px-4 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    cn(
                      "text-lg font-semibold py-2 transition-colors",
                      isActive ? "text-primary" : "text-foreground"
                    )
                  }
                >
                  {link.name}
                </NavLink>
              ))}
              <hr className="border-border my-2" />
              <div className="flex flex-col gap-4">
                <a href={`tel:${CONTACT_DATA.phone}`} className="flex items-center gap-3 text-sm font-medium">
                  <Phone className="w-4 h-4 text-primary" />
                  {formatPhoneNumber(CONTACT_DATA.phone)}
                </a>
                <Button asChild size="lg" className="w-full">
                  <Link to={ROUTE_PATHS.CONTACT}>Book Appointment</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-card border-t border-border">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand Section */}
            <div className="space-y-6">
              <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-2">
                <div className="bg-primary p-1.5 rounded-md">
                  <Cpu className="w-5 h-5 text-primary-foreground" />
                </div>
                <span className="text-xl font-bold tracking-tight text-foreground">
                  TECH<span className="text-primary">RESCUE</span>
                </span>
              </Link>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Your trusted partner for 24/7 onsite electronics repair and professional IT support. 
                We bring certified expertise directly to your doorstep.
              </p>
              <div className="flex items-center gap-4">
                <a href="#" className="p-2 rounded-full bg-accent hover:bg-primary hover:text-white transition-all">
                  <SiFacebook className="w-4 h-4" />
                </a>
                <a href="#" className="p-2 rounded-full bg-accent hover:bg-primary hover:text-white transition-all">
                  <SiX className="w-4 h-4" />
                </a>
                <a href="#" className="p-2 rounded-full bg-accent hover:bg-primary hover:text-white transition-all">
                  <SiInstagram className="w-4 h-4" />
                </a>
                <a href="#" className="p-2 rounded-full bg-accent hover:bg-primary hover:text-white transition-all">
                  <SiLinkedin className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider mb-6 text-foreground">Our Services</h3>
              <ul className="space-y-3">
                <li>
                  <Link to={ROUTE_PATHS.SERVICES} className="text-sm text-muted-foreground hover:text-primary flex items-center gap-2 group">
                    <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                    TV & Home Cinema Repair
                  </Link>
                </li>
                <li>
                  <Link to={ROUTE_PATHS.SERVICES} className="text-sm text-muted-foreground hover:text-primary flex items-center gap-2 group">
                    <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                    Appliance Maintenance
                  </Link>
                </li>
                <li>
                  <Link to={ROUTE_PATHS.SERVICES} className="text-sm text-muted-foreground hover:text-primary flex items-center gap-2 group">
                    <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                    Laptop & Desktop Upgrades
                  </Link>
                </li>
                <li>
                  <Link to={ROUTE_PATHS.SERVICES} className="text-sm text-muted-foreground hover:text-primary flex items-center gap-2 group">
                    <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                    Network & Internet Issues
                  </Link>
                </li>
                <li>
                  <Link to={ROUTE_PATHS.SERVICES} className="text-sm text-muted-foreground hover:text-primary flex items-center gap-2 group">
                    <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                    Remote IT Support
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider mb-6 text-foreground">Contact Us</h3>
              <ul className="space-y-4">
                <li className="flex gap-3">
                  <Phone className="w-5 h-5 text-primary shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase">Call Us 24/7</p>
                    <a href={`tel:${CONTACT_DATA.phone}`} className="text-sm font-medium hover:text-primary">
                      {formatPhoneNumber(CONTACT_DATA.phone)}
                    </a>
                  </div>
                </li>
                <li className="flex gap-3">
                  <Mail className="w-5 h-5 text-primary shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase">Email Support</p>
                    <a href={`mailto:${CONTACT_DATA.email}`} className="text-sm font-medium hover:text-primary">
                      {CONTACT_DATA.email}
                    </a>
                  </div>
                </li>
                <li className="flex gap-3">
                  <MapPin className="w-5 h-5 text-primary shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase">Main Office</p>
                    <p className="text-sm text-muted-foreground">{CONTACT_DATA.address}</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Service Areas */}
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider mb-6 text-foreground">Service Areas</h3>
              <div className="flex flex-wrap gap-2">
                {CONTACT_DATA.coverageAreas.map((area) => (
                  <span 
                    key={area} 
                    className="px-3 py-1 bg-accent rounded-full text-[10px] font-bold uppercase text-accent-foreground"
                  >
                    {area}
                  </span>
                ))}
              </div>
              <div className="mt-8 p-4 bg-primary/5 rounded-xl border border-primary/10">
                <div className="flex items-center gap-3 mb-2">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                  <span className="text-sm font-bold">Certified Experts</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  All technicians are background-checked and manufacturer-certified for your peace of mind.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              &copy; 2026 TechRescue Services Inc. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <Link to="#" className="hover:text-primary">Privacy Policy</Link>
              <Link to="#" className="hover:text-primary">Terms of Service</Link>
              <Link to="#" className="hover:text-primary">Warranty Info</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Call Button (Mobile Only) */}
      <div className="md:hidden fixed bottom-6 right-6 z-40">
        <a
          href={`tel:${CONTACT_DATA.phone}`}
          className="flex items-center justify-center w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg animate-bounce"
        >
          <Phone className="w-6 h-6" />
        </a>
      </div>
    </div>
  );
}
